import { ApiResponse, FetchParameters } from './types';

export class ApiClient {
  protected static _refreshUrl?: string | null;
  protected static _logout?: () => Promise<void> | null;

  public static get errorCodes() {
    return {
      network: 1,
      jsonParse: 2,
      unauthorised: 401,
      badRequest: 400,
      notFound: 404,
      tooManyRequests: 429,
      invalidToken: 498,
      serverError: 500,
    };
  }

  protected static get requestHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };
  }

  /*
    Gets the auth token expiry from a cookie.
      Returns the expiry date if it's found and can be parsed.
        Returns the earliest EPOC date if not, so that _shouldRefreshToken will always be true.
   */
  protected static get tokenExpiry(): Date {
    const tokenInfoRegex = new RegExp('.auth.token_info=([^;]+)', 'g');
    try {
      const tokenInfo = JSON.parse(atob(tokenInfoRegex.exec(document.cookie)?.[1] ?? ''));
      const expiry = new Date(tokenInfo?.at_exp * 1000);

      return isNaN(expiry?.getTime()) ? new Date(0) : expiry;
    } catch (err) {
      return new Date(0);
    }
  }

  /*
    Returns true if the auth token has or is about to expire(d)
   */
  private static get _shouldRefreshToken(): boolean {
    const refreshWindow = 2 * 60000; // If the token will be expired 2 minutes from now, we should refresh
    return Date.now() + refreshWindow >= this.tokenExpiry.getTime();
  }

  /*
      To be run before all authorised requests to unified customer / other APIs
      Checks if the current access token,
   */
  private static async _refreshToken(): Promise<boolean> {
    if (!this._refreshUrl) return false;
    const response = await fetch(this._refreshUrl, {
      // This endpoint will automatically put the new token in a http-only cookie in the response
      headers: this.requestHeaders,
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok && this._logout) {
      await this._logout();
      return false;
    }
    return true;
  }

  protected static _shouldRetryRequest(responseStatusCode: number, haveRetried?: boolean) {
    switch (true) {
      case responseStatusCode >= 200 && responseStatusCode <= 299:
      case haveRetried: // have already retried
      case responseStatusCode === this.errorCodes.badRequest:
      case responseStatusCode === this.errorCodes.notFound:
      case responseStatusCode === this.errorCodes.serverError:
      case responseStatusCode === this.errorCodes.tooManyRequests:
      case responseStatusCode === this.errorCodes.invalidToken:
        return false;
      default:
        return true;
    }
  }

  protected static async _fetch<T>({
    path,
    method = 'GET',
    body = undefined,
    isRetry = false,
    signal,
  }: FetchParameters): Promise<ApiResponse<T>> {
    try {
      if (this._refreshUrl && (isRetry || this._shouldRefreshToken)) {
        if (!(await this._refreshToken())) {
          return { error: { message: 'Failed to refresh token' }, statusCode: this.errorCodes.unauthorised };
        }
      }

      const response = await fetch(path, {
        method,
        headers: this.requestHeaders,
        credentials: this._refreshUrl ? 'include' : 'omit',
        body: body ? JSON.stringify(body) : null,
        signal,
      });

      if (response.ok) {
        const result = await response
          .clone()
          .json()
          .catch(() => {
            return {}; // Catching the .json(), if the body is empty 'response' will then be an empty object
          });
        return { data: result, statusCode: response?.status };
      } else if (this._shouldRetryRequest(response?.status, isRetry)) {
        return await this._fetch({ path, method, body, isRetry: true });
      }

      const responseText = await response.text();
      return { error: { message: responseText }, statusCode: response.status };
    } catch (error: any) {
      return {
        statusCode: typeof error === 'number' ? error : this.errorCodes.network,
        error: { message: error?.message ?? `${error}` },
      };
    }
  }
}
